import { memo, useCallback, useState } from 'react';
import { IconButton, Stack } from '@mui/material';
import { FlowStop as FlowStopType } from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { MinusIcon, PlusIcon } from '@/assets/icons';
import { TargetInputCanvas as TargetInput } from '@/modules/common/components/inputs/components/TargetInputCanvas';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';

type AreaItem = { value: string; label: string };

type Props = {
  flowStop: FlowStopType | null;
  allAreaItems: AreaItem[];
  selectableAreaItems: AreaItem[];
  /** Translated noun for the load handling type of this flow stop, e.g. "intake area". */
  areaType?: string;
  disabled?: boolean;
  onSelectArea?: (flowStop: FlowStopType, areaId: string) => void;
  onOverwriteArea?: (flowStop: FlowStopType, areaId: string, previousAreaId: string) => void;
  onRemoveArea: (flowStop: FlowStopType, areaId: string) => void;
  onRemoveFlowStop: (flowStop: FlowStopType) => void;
  onRename: (flowStop: FlowStopType, name: string) => void;
  onCreate?: (areaId: string) => void;
  onDismissDraft?: () => void;
};

function FlowStopComponent({
  flowStop,
  allAreaItems,
  selectableAreaItems,
  areaType,
  disabled = false,
  onSelectArea,
  onOverwriteArea,
  onRemoveArea,
  onRemoveFlowStop,
  onRename,
  onCreate,
  onDismissDraft,
}: Props) {
  const { t } = useTranslation('interface');
  const isDraft = onDismissDraft !== undefined;
  const [showAddArea, setShowAddArea] = useState(() => !flowStop || flowStop.areaIds.length === 0);

  const handleSelectExistingArea = useCallback(
    (selectedAreaId: string, previousAreaId: string) => {
      if (disabled) return;
      if (flowStop && onOverwriteArea) onOverwriteArea(flowStop, selectedAreaId, previousAreaId);
    },
    [disabled, flowStop, onOverwriteArea],
  );

  const handleAddAreaSelect = useCallback(
    (areaId: string) => {
      if (disabled) return;

      if (flowStop && onSelectArea) {
        onSelectArea(flowStop, areaId);
        return;
      }

      onCreate?.(areaId);
    },
    [disabled, flowStop, onCreate, onSelectArea],
  );

  const handleRemoveArea = useCallback(
    (areaId: string) => {
      if (disabled) return;
      if (flowStop) onRemoveArea(flowStop, areaId);
    },
    [disabled, flowStop, onRemoveArea],
  );

  const handleRemoveTop = useCallback(() => {
    if (disabled) return;

    if (flowStop) {
      onRemoveFlowStop(flowStop);
      return;
    }

    onDismissDraft?.();
  }, [disabled, flowStop, onDismissDraft, onRemoveFlowStop]);

  const handleCancelAddArea = useCallback(() => {
    if (disabled) return;

    if (isDraft) {
      onDismissDraft?.();
      return;
    }

    setShowAddArea(false);
  }, [disabled, isDraft, onDismissDraft]);

  const handleNameChange = useCallback(
    (newName: string) => {
      if (disabled) return;
      if (flowStop && newName.trim().length > 0) onRename(flowStop, newName);
    },
    [disabled, flowStop, onRename],
  );

  const handleShowAddArea = useCallback(() => {
    if (disabled) return;

    setShowAddArea(true);
  }, [disabled]);

  const title = flowStop?.name
    ? flowStop.name
    : t('interface:properties.flow_stops.add_tooltip', 'New Flow Stop');

  return (
    <Stack gap={1} direction='column'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <PropertiesTitle
          value={title}
          editable={!disabled && !!flowStop}
          fontSize={14}
          onChange={disabled ? undefined : handleNameChange}
          sx={{ flex: '1 1 auto', alignItems: 'center' }}
        />

        <IconButton
          onClick={disabled ? undefined : handleRemoveTop}
          size='small'
          disabled={disabled}
          sx={{ p: 0, height: 'fit-content' }}
        >
          <MinusIcon />
        </IconButton>
      </Stack>

      {flowStop?.areaIds.map((areaId, index) => {
        const currentArea = allAreaItems.find((item) => item.value === areaId);
        const dropdownItems = currentArea
          ? [currentArea, ...selectableAreaItems]
          : selectableAreaItems;

        return (
          <PropertiesLabel
            key={`area-${areaId}`}
            skipFocus
            i18nkey='interface:properties.flow_stops.area'
            amount={index + 1}
          >
            <TargetInput
              value={areaId}
              disabled={disabled}
              onRemove={() => handleRemoveArea(areaId)}
              disableRemove={disabled || flowStop.areaIds.length === 1}
              onTargetSelect={(selectedAreaId) => handleSelectExistingArea(selectedAreaId, areaId)}
              dropdownItems={disabled ? [] : dropdownItems}
              areaType={areaType}
            />
          </PropertiesLabel>
        );
      })}

      {showAddArea && (
        <PropertiesLabel
          key='add-area'
          skipFocus
          i18nkey='interface:properties.flow_stops.area'
          amount={(flowStop?.areaIds.length ?? 0) + 1}
        >
          <TargetInput
            value=''
            disabled={disabled}
            onRemove={handleCancelAddArea}
            onTargetSelect={handleAddAreaSelect}
            onPickerCancel={handleCancelAddArea}
            dropdownItems={disabled ? [] : selectableAreaItems}
            activateOnStart={!disabled}
            areaType={areaType}
          />
        </PropertiesLabel>
      )}

      <Stack justifyContent='flex-end' alignItems='center' direction='row'>
        <IconButton
          onClick={disabled ? undefined : handleShowAddArea}
          size='small'
          disabled={disabled || showAddArea}
          sx={{ p: 0, height: 'fit-content' }}
        >
          <PlusIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export const FlowStop = memo(FlowStopComponent);
